import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Conversation, Message, DavidMessageResponse } from '../types';
import { messagingServiceApi } from '../services';
import ChatHeader from './ChatHeader';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';
import DateSeparator from './DateSeparator';
import TypingIndicator from './TypingIndicator';
import JobDetailsSidebar from './JobDetailsSidebar';
import JobEditSidebar from './JobEditSidebar';
import { isSameDay, getDateSeparatorLabel } from '../utils/timeUtils';

// WebSocket reconnect constants
const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_BASE_DELAY_MS = 1000;

interface ChatViewProps {
  /**
   * Selected conversation
   */
  conversation: Conversation;

  /**
   * Current user ID
   */
  currentUserId: string;

  /**
   * User role
   */
  userRole: 'candidate' | 'recruiter';

  /**
   * Job details (optional - fetched if not provided)
   */
  jobDetails?: any;

  /**
   * Callback when conversation is starred/unstarred
   */
  onStarToggle?: (conversationId: string) => void;

  /**
   * Is conversation starred
   */
  isStarred?: boolean;

  /**
   * Callback when a job recommendation is clicked (opens detail panel in parent)
   */
  onJobClick?: (jobId: string) => void;
}

/**
 * ChatView Component
 * Complete chat interface with messages, input, and job details
 */
const ChatView: React.FC<ChatViewProps> = ({
  conversation,
  currentUserId,
  userRole,
  jobDetails,
  onStarToggle,
  isStarred = false,
  onJobClick,
}) => {
  // Messages state
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  // 7c: stage-gate state (set when server returns 403)
  const [stageGateBlocked, setStageGateBlocked] = useState(false);

  // Pagination state
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [oldestCursor, setOldestCursor] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Reply state
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);

  // Sidebar visibility state
  const [isInfoSidebarOpen, setIsInfoSidebarOpen] = useState(false);

  // Sidebar width (draggable)
  const [sidebarWidth, setSidebarWidth] = useState(420);
  const isSidebarDragging = useRef(false);

  const handleSidebarDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isSidebarDragging.current = true;
    const startX = e.clientX;
    const startWidth = sidebarWidth;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev: MouseEvent) => {
      if (!isSidebarDragging.current) return;
      const delta = startX - ev.clientX; // drag left = wider
      const newWidth = Math.min(640, Math.max(300, startWidth + delta));
      setSidebarWidth(newWidth);
    };

    const onMouseUp = () => {
      isSidebarDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [sidebarWidth]);

  // WebSocket state
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const isMountedRef = useRef(true);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Typing indicator state
  const [isOtherUserTyping, setIsOtherUserTyping] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  /**
   * Scroll to bottom of messages
   */
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Whether this is a David (bot) conversation
  const isDavid = conversation.is_david === true;

  /**
   * Fetch messages on conversation change
   */
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setIsLoadingMessages(true);
        setMessagesError(null);
        setStageGateBlocked(false);
        setHasMoreMessages(false);
        setOldestCursor(null);

        if (isDavid && conversation.david_conversation_id) {
          // David conversations: fetch via REST from /david/messages
          const davidMessages = await messagingServiceApi.getDavidMessages(conversation.david_conversation_id);
          const msgs: Message[] = davidMessages.map((dm: DavidMessageResponse) => ({
            id: dm.id,
            conversation_id: dm.conversation_id,
            sender_id: dm.sender_type === 'david_bot' ? 'david' : currentUserId,
            sender_role: 'recruiter' as const, // David messages appear as "received"
            content: dm.content,
            message_type: dm.message_type as Message['message_type'],
            job_recommendations: dm.job_recommendations || [],
            feedback: dm.feedback,
            quick_response: dm.quick_response,
            timestamp: dm.created_at,
            status: 'read' as const,
            read_by: { candidate: true, recruiter: true },
          }));
          setMessages(msgs);
        } else {
          // Regular conversations: fetch via standard messaging API
          const response = await messagingServiceApi.getMessages({
            application_id: conversation.application_id,
            limit: 50,
          });

          // Backend returns DESC (newest first); reverse so oldest is at the top.
          const msgs = (response.messages || []).slice().reverse();
          setMessages(msgs);
          setHasMoreMessages(response.has_more || false);
          // response.before is the ObjectID of the oldest message — use as scroll-up cursor.
          setOldestCursor(response.before || null);

          // Mark conversation as read
          if (
            (userRole === 'candidate' && conversation.unread_count.candidate > 0) ||
            (userRole === 'recruiter' && conversation.unread_count.recruiter > 0)
          ) {
            await messagingServiceApi.markConversationAsRead(conversation.application_id);
          }
        }

        // Scroll to bottom after messages load
        setTimeout(scrollToBottom, 100);
      } catch (err: any) {
        console.error('Failed to fetch messages:', err);
        // 7c: Detect stage-gate 403 from messaging service
        if (err?.response?.status === 403) {
          setStageGateBlocked(true);
        }
        // For virtual conversations that don't exist yet, initialize with empty array
        setMessages([]);
        setMessagesError(null); // Don't show error for virtual conversations
      } finally {
        setIsLoadingMessages(false);
      }
    };

    fetchMessages();
  }, [conversation.application_id, userRole, isDavid, conversation.david_conversation_id, currentUserId]);

  /**
   * Load older messages (scroll-to-top pagination)
   */
  const loadMoreMessages = useCallback(async () => {
    if (!hasMoreMessages || isLoadingMore || !oldestCursor) return;

    try {
      setIsLoadingMore(true);
      const container = messagesContainerRef.current;
      const prevScrollHeight = container?.scrollHeight || 0;

      const response = await messagingServiceApi.getMessages({
        application_id: conversation.application_id,
        before: oldestCursor,
        limit: 50,
      });

      // Backend returns DESC; reverse the batch so it is oldest-first before prepending.
      const olderMessages = (response.messages || []).slice().reverse();
      setMessages((prev) => [...olderMessages, ...prev]);
      // response.before points to the oldest message in this batch — advance cursor for next page.
      setOldestCursor(response.before || null);
      setHasMoreMessages(response.has_more || false);

      // Restore scroll position so user stays at same message
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight - prevScrollHeight;
        }
      });
    } catch (err) {
      console.error('Failed to load more messages:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMoreMessages, isLoadingMore, oldestCursor, conversation.application_id]);

  /**
   * Scroll listener — triggers pagination when user scrolls near the top
   */
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (container.scrollTop < 100) {
        loadMoreMessages();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [loadMoreMessages]);

  /**
   * WebSocket connection with exponential backoff reconnect
   * Skipped for David conversations (REST-only)
   */
  useEffect(() => {
    if (isDavid) return; // No WebSocket for David conversations

    isMountedRef.current = true;
    reconnectAttemptsRef.current = 0;

    const connectWebSocket = () => {
      if (!isMountedRef.current) return;

      try {
        const wsUrl = messagingServiceApi.getWebSocketUrl(conversation.application_id);
        const websocket = new WebSocket(wsUrl);
        wsRef.current = websocket;

        websocket.onopen = () => {
          if (!isMountedRef.current) return;
          console.log('WebSocket connected for conversation:', conversation.application_id);
          reconnectAttemptsRef.current = 0;
          setIsConnected(true);
        };

        websocket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            if (data.type === 'new_message') {
              const newMessage: Message = data.message;
              setMessages((prev) => {
                // Avoid duplicates by server-assigned ID
                if (prev.some((m) => m.id === newMessage.id)) return prev;
                return [...prev, newMessage];
              });
              setTimeout(scrollToBottom, 100);
            } else if (data.type === 'message_confirmed') {
              // Server confirmed our sent message: replace the optimistic temp message
              // with the real one (which has a permanent id, sender_role, read_by, etc.)
              const confirmedMessage: Message = { ...data.message, status: 'sent' };
              const tempId: string | undefined = data.temp_id;
              setMessages((prev) => {
                if (tempId && prev.some((m) => m.id === tempId)) {
                  return prev.map((m) => (m.id === tempId ? confirmedMessage : m));
                }
                // Guard against duplicate if confirmation arrives after a reconnect
                if (prev.some((m) => m.id === confirmedMessage.id)) return prev;
                return prev;
              });
            } else if (data.type === 'message_read') {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === data.message_id
                    ? {
                        ...msg,
                        read_by: {
                          ...msg.read_by,
                          [userRole === 'candidate' ? 'recruiter' : 'candidate']: true,
                        },
                      }
                    : msg
                )
              );
            } else if (data.type === 'user_typing') {
              setIsOtherUserTyping(true);
              setTimeout(scrollToBottom, 100);
              if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
              typingTimeoutRef.current = setTimeout(() => setIsOtherUserTyping(false), 3000);
            } else if (data.type === 'user_stopped_typing') {
              setIsOtherUserTyping(false);
              if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        websocket.onerror = (error) => {
          console.error('WebSocket error:', error);
          setIsConnected(false);
        };

        websocket.onclose = () => {
          if (!isMountedRef.current) return;
          console.log('WebSocket disconnected');
          setIsConnected(false);

          if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
            const delay = Math.min(
              RECONNECT_BASE_DELAY_MS * Math.pow(2, reconnectAttemptsRef.current),
              30000
            );
            console.log(
              `Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/${MAX_RECONNECT_ATTEMPTS})`
            );
            reconnectAttemptsRef.current++;
            reconnectTimeoutRef.current = setTimeout(connectWebSocket, delay);
          } else {
            console.error('Max WebSocket reconnect attempts reached');
          }
        };
      } catch (error) {
        console.error('Failed to connect WebSocket:', error);
      }
    };

    connectWebSocket();

    return () => {
      isMountedRef.current = false;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [conversation.application_id, isDavid]);

  /**
   * Send message with optimistic UI, file upload support, and deduplication
   */
  const handleSendMessage = async (content: string, attachments?: File[]) => {
    if (!content.trim() && !attachments?.length) return;

    const tempId = `temp_${Date.now()}`;

    // Capture reply context before clearing state (used below and in closure)
    const currentReplyingTo = replyingTo;
    setReplyingTo(null);

    // Add optimistic message immediately
    const optimisticMsg: Message = {
      id: tempId,
      conversation_id: conversation.application_id,
      sender_id: currentUserId,
      sender_role: userRole,
      content: content.trim(),
      timestamp: new Date().toISOString(),
      status: 'sending',
      read_by: { candidate: false, recruiter: false },
      // 7g: Include reply-to metadata for quoted preview rendering
      ...(currentReplyingTo && {
        metadata: {
          reply_to_id: currentReplyingTo.id,
          reply_to_content: currentReplyingTo.content,
          reply_to_sender: currentReplyingTo.sender_id === currentUserId ? 'You' : getDisplayName(),
        },
      }),
    };
    setMessages((prev) => [...prev, optimisticMsg]);
    setTimeout(scrollToBottom, 100);

    try {
      setIsSending(true);

      // Upload file attachments first
      const uploadedAttachments: Array<{ url: string; name: string; size: number; type: string }> = [];
      if (attachments?.length) {
        for (const file of attachments) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('application_id', conversation.application_id);
          formData.append('sender_id', currentUserId);
          formData.append('sender_role', userRole);

          const uploadResponse = await fetch(
            `${import.meta.env.VITE_MESSAGING_SERVICE_URL}/messages/file`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${localStorage.getItem('authToken')}`,
              },
              body: formData,
            }
          );

          if (!uploadResponse.ok) throw new Error(`Failed to upload file: ${file.name}`);
          const uploadData = await uploadResponse.json();
          uploadedAttachments.push(uploadData.attachment);
        }
      }

      const currentWs = wsRef.current;
      if (currentWs && currentWs.readyState === WebSocket.OPEN) {
        const messagePayload: Record<string, any> = {
          type: 'send_message',
          temp_id: tempId,
          content: content.trim(),
          sender_id: currentUserId,
          sender_role: userRole,
          timestamp: new Date().toISOString(),
        };
        if (currentReplyingTo) {
          messagePayload.reply_to_id = currentReplyingTo.id;
          // 7g: Include content for backend to store and return in metadata
          messagePayload.reply_to_content = currentReplyingTo.content;
        }
        if (uploadedAttachments.length) messagePayload.attachments = uploadedAttachments;

        currentWs.send(JSON.stringify(messagePayload));

        // Mark optimistic message as sent
        setMessages((prev) =>
          prev.map((m) => (m.id === tempId ? { ...m, status: 'sent' } : m))
        );
      } else {
        throw new Error('WebSocket is not connected');
      }
    } catch (error: any) {
      console.error('Failed to send message:', error);
      // Mark optimistic message as failed
      setMessages((prev) =>
        prev.map((m) => (m.id === tempId ? { ...m, status: 'failed' } : m))
      );
    } finally {
      setIsSending(false);
    }
  };

  /**
   * Send voice message
   */
  const handleSendVoiceMessage = async (audioBlob: Blob, duration: number) => {
    try {
      setIsSending(true);

      const formData = new FormData();
      formData.append('audio', audioBlob, `voice-${Date.now()}.webm`);
      formData.append('duration', duration.toString());
      formData.append('application_id', conversation.application_id);
      formData.append('sender_id', currentUserId);
      formData.append('sender_role', userRole);

      const response = await fetch(
        `${import.meta.env.VITE_MESSAGING_SERVICE_URL}/messages/voice`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
          body: formData,
        }
      );

      if (!response.ok) throw new Error('Failed to upload voice message');

      const data = await response.json();
      console.log('Voice message uploaded:', data);

      const currentWs = wsRef.current;
      if (currentWs && currentWs.readyState === WebSocket.OPEN) {
        currentWs.send(
          JSON.stringify({
            type: 'voice_message_sent',
            message_id: data.message_id,
          })
        );
      }
    } catch (error: any) {
      console.error('Failed to send voice message:', error);
      alert('Failed to send voice message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  /**
   * Set message as reply target
   */
  const handleReply = (message: Message) => {
    setReplyingTo(message);
  };

  /**
   * 7d: Retry a failed message — remove the failed copy and resend the content
   */
  const handleRetry = (message: Message) => {
    setMessages((prev) => prev.filter((m) => m.id !== message.id));
    handleSendMessage(message.content);
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  const handleFeedback = async (messageId: string, type: 'thumbs_up' | 'thumbs_down') => {
    try {
      await messagingServiceApi.submitFeedback(messageId, type);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    }
  };

  const handleQuickResponse = async (messageId: string, text: string) => {
    try {
      await messagingServiceApi.submitQuickResponse(messageId, text);
    } catch (err) {
      console.error('Failed to submit quick response:', err);
    }
  };

  /**
   * Get display name based on role
   */
  const getDisplayName = (): string => {
    if (isDavid) return 'David';
    return userRole === 'candidate'
      ? conversation.company_name || 'Unknown Company'
      : conversation.candidate_name || 'Unknown Candidate';
  };

  /**
   * Check if message was sent by current user
   */
  const isMessageSent = (message: Message): boolean => {
    return message.sender_id === currentUserId;
  };

  /**
   * Render messages list with date separators and typing indicator
   */
  const renderMessages = (): React.ReactNode[] => {
    const elements: React.ReactNode[] = [];

    messages.forEach((message, index) => {
      const prevMessage = index > 0 ? messages[index - 1] : null;
      const needsDateSeparator =
        !prevMessage || !isSameDay(prevMessage.timestamp, message.timestamp);

      if (needsDateSeparator) {
        elements.push(
          <DateSeparator
            key={`date-${message.id}`}
            label={getDateSeparatorLabel(message.timestamp)}
          />
        );
      }

      elements.push(
        <MessageBubble
          key={message.id}
          message={message}
          isSent={isMessageSent(message)}
          senderName={!isMessageSent(message) ? getDisplayName() : undefined}
          senderAvatar={
            !isMessageSent(message) && userRole === 'candidate'
              ? conversation.company_logo
              : undefined
          }
          onReply={handleReply}
          onRetry={handleRetry}
          isDavidMessage={isDavid}
          onFeedback={isDavid ? handleFeedback : undefined}
          onQuickResponse={isDavid ? handleQuickResponse : undefined}
          onJobClick={isDavid ? onJobClick : undefined}
        />
      );
    });

    // Typing indicator at the end of message list
    if (isOtherUserTyping) {
      elements.push(
        <TypingIndicator
          key="typing-indicator"
          userName={getDisplayName()}
          avatar={userRole === 'candidate' ? conversation.company_logo : undefined}
        />
      );
    }

    return elements;
  };

  return (
    <div className="flex flex-row w-full h-full bg-white overflow-hidden">
      {/* Left: Chat area */}
      <div className="flex flex-col flex-1 min-w-0 h-full overflow-hidden">
        {/* Chat Header */}
        <ChatHeader
          displayName={getDisplayName()}
          avatar={userRole === 'candidate' ? conversation.company_logo : undefined}
          isOnline={conversation.is_online}
          lastSeen={conversation.last_message_at}
          isStarred={isStarred}
          isInfoOpen={isInfoSidebarOpen}
          isDavid={isDavid}
          onStarToggle={() => onStarToggle?.(conversation.application_id)}
          onProfileClick={() => setIsInfoSidebarOpen(!isInfoSidebarOpen)}
        />

        {/* Messages area */}
        <div
          ref={messagesContainerRef}
          className="flex-1 min-h-0 overflow-y-auto px-6 py-4 bg-gradient-to-b from-[#FAFBFC] to-[#F3F4F6] overscroll-contain"
        >
          {isLoadingMessages ? (
            /* Loading state */
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-2">
                <div className="w-8 h-8 border-4 border-[#015FA6] border-t-transparent rounded-full animate-spin" />
                <p className="font-manrope font-medium text-sm text-[#757575]">
                  Loading messages...
                </p>
              </div>
            </div>
          ) : stageGateBlocked ? (
            /* 7c: Stage-gate state — messaging not available at current application stage */
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-4 px-6 max-w-sm text-center">
                <div className="w-16 h-16 rounded-2xl bg-[#FFF7ED] flex items-center justify-center">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#F59E0B" strokeWidth="2"/>
                    <path d="M12 8V12" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="12" cy="16" r="1" fill="#F59E0B"/>
                  </svg>
                </div>
                <div>
                  <p className="font-manrope font-semibold text-base text-[#374151]">
                    Messaging not available
                  </p>
                  <p className="font-manrope font-normal text-sm text-[#6B7280] mt-1">
                    Messaging is unlocked at a later stage of the application process.
                    Check back once your application progresses.
                  </p>
                </div>
              </div>
            </div>
          ) : messagesError ? (
            /* Error state */
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-2 px-4">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="12" r="10" stroke="#DC0000" strokeWidth="2" />
                  <path d="M12 8V12" stroke="#DC0000" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="12" cy="16" r="1" fill="#DC0000" />
                </svg>
                <p className="font-manrope font-medium text-sm text-[#DC0000] text-center">
                  {messagesError}
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-2 px-4 py-2 bg-[#015FA6] text-white rounded-lg font-manrope font-medium text-sm hover:bg-[#014580] transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : messages.length === 0 ? (
            /* Empty state */
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-4 px-6">
                {/* Chat bubble illustration */}
                <div className="relative">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#015FA6]/10 to-[#015FA6]/5 flex items-center justify-center">
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
                        stroke="#015FA6"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="8" cy="10" r="1" fill="#015FA6"/>
                      <circle cx="12" cy="10" r="1" fill="#015FA6"/>
                      <circle cx="16" cy="10" r="1" fill="#015FA6"/>
                    </svg>
                  </div>
                </div>
                <div className="text-center">
                  <p className="font-manrope font-semibold text-base text-[#374151]">
                    No messages yet
                  </p>
                  <p className="font-manrope font-normal text-sm text-[#9CA3AF] mt-1">
                    Start the conversation by sending a message
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* Messages list */
            <div className="flex flex-col">
              {/* Load-more indicator at the top */}
              {isLoadingMore && (
                <div className="flex justify-center py-3">
                  <div className="w-5 h-5 border-2 border-[#015FA6] border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {hasMoreMessages && !isLoadingMore && (
                <div className="flex justify-center py-2">
                  <p className="font-manrope text-xs text-[#9CA3AF]">
                    Scroll up to load older messages
                  </p>
                </div>
              )}

              {renderMessages()}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Reply context bar */}
        {replyingTo && (
          <div className="flex-shrink-0 flex items-center gap-3 px-4 py-2 bg-[#F8F9FA] border-t border-[#E5E7EB]">
            <div className="flex-1 min-w-0 pl-3 border-l-2 border-[#015FA6]">
              <p className="font-manrope font-medium text-xs text-[#015FA6] mb-0.5">
                Replying to {isMessageSent(replyingTo) ? 'yourself' : getDisplayName()}
              </p>
              <p className="font-manrope font-normal text-xs text-[#6B7280] truncate">
                {replyingTo.content}
              </p>
            </div>
            <button
              onClick={handleCancelReply}
              className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-[#E5E7EB] transition-colors flex-shrink-0"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="#6B7280"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Message Input — hidden when stage-gated or David conversation */}
        {isDavid ? (
          <div className="flex-shrink-0 flex items-center justify-center px-4 py-3 bg-[#F8F9FA] border-t border-[#E5E7EB]">
            <div className="flex items-center gap-2 text-[#9CA3AF]">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M12 16V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="12" cy="8" r="1" fill="currentColor"/>
              </svg>
              <span className="font-manrope text-sm">Automated job recommendations from David</span>
            </div>
          </div>
        ) : !stageGateBlocked ? (
          <MessageInput
            onSendMessage={handleSendMessage}
            onSendVoiceMessage={handleSendVoiceMessage}
            isSending={isSending}
          />
        ) : null}
      </div>

      {/* Right: Sidebar (Job Details for candidate, Job Edit for recruiter) - Only shown when profile is clicked */}
      {isInfoSidebarOpen && (
        <div className="flex flex-row flex-shrink-0 h-full animate-slide-in-right" style={{ width: sidebarWidth }}>
          {/* Drag handle on left edge */}
          <div
            onMouseDown={handleSidebarDragStart}
            className="w-1.5 h-full bg-[#E5E7EB] hover:bg-[#015FA6]/40 cursor-col-resize flex-shrink-0 transition-colors duration-150"
          />
          <div className="flex-1 min-w-0 h-full border-l border-[#E5E7EB] bg-white overflow-hidden">
          {userRole === 'candidate' && jobDetails ? (
            <JobDetailsSidebar
              jobDetails={jobDetails}
              similarJobs={jobDetails.similar_jobs}
              onClose={() => setIsInfoSidebarOpen(false)}
            />
          ) : userRole === 'recruiter' ? (
            <JobEditSidebar
              jobData={jobDetails}
              onSave={(data) => {
                console.log('Save job data:', data);
                // TODO: Implement API call to save job data
              }}
              onClose={() => setIsInfoSidebarOpen(false)}
            />
          ) : (
            /* Fallback info panel */
            <div className="flex flex-col h-full">
              {/* Header with close button */}
              <div className="flex-shrink-0 flex items-center justify-between px-4 py-4 border-b border-[#E5E7EB]">
                <h3 className="font-manrope font-semibold text-base text-[#1A1A1A]">Details</h3>
                <button
                  onClick={() => setIsInfoSidebarOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F1F3F4] transition-colors"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>

              {/* Company/Candidate Info */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="flex flex-col items-center gap-4 py-6">
                  {/* Avatar */}
                  {conversation.company_logo ? (
                    <img
                      src={conversation.company_logo}
                      alt={getDisplayName()}
                      className="w-20 h-20 rounded-2xl object-cover shadow-sm"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#015FA6] to-[#0176C9] flex items-center justify-center shadow-sm">
                      <span className="font-manrope font-bold text-2xl text-white">
                        {getDisplayName().charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}

                  <div className="text-center">
                    <h4 className="font-manrope font-semibold text-lg text-[#1A1A1A]">
                      {getDisplayName()}
                    </h4>
                    {conversation.job_title && (
                      <p className="font-manrope font-normal text-sm text-[#6B7280] mt-1">
                        {conversation.job_title}
                      </p>
                    )}
                    <p className={`font-manrope font-medium text-xs mt-2 ${conversation.is_online ? 'text-[#22C55E]' : 'text-[#9CA3AF]'}`}>
                      {conversation.is_online ? 'Online' : 'Offline'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatView;
